import classNames from "classnames";
import Header from "./Header";
import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ContractContext } from "../../../providers/contract";
import {
  AddOnFees,
  EContractType,
  ESenderRole,
  FeeCalculationType,
} from "../../../setup/api/services/contract/models";
import BarChart from "./BarChart";
import PartyTable from "../../business/create/partyTable";
import { ECONTRACT_STATUS, EPARTY_STATUS } from "../../../enums";
import { AuthConfig } from "../../../setup/context/config/authConfig";
import PrimaryButton from "../../../components/common/PrimaryButton";
import AlertWidget from "../../../components/common/AlertWidget";
import SettlementConfirmationModal from "./SettlementConfirmationModal";
import SettlementJourneyModal from "./SettlementJourneyModal";
import { EContractActions } from "../../../setup/api/services/contractActions/enum";
import { externalDoAction } from "../../../setup/api/services/contractActions";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../setup/constants";
import AmountCard from "./AmountCard";

const SettlementDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { contract } = useContext(ContractContext);
  const userIdentification = AuthConfig.getUserInfo()?.userIdentification;
  const accountId = contract?.organization?.accountId;
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showSettlementError, setShowSettlementError] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [showJourneyModal, setShowJourneyModal] = useState(false);

  useEffect(() => {
    // Check if the user has seen the journey modal before
    const hasSeenJourneyModal = localStorage.getItem(
      "settlement_journey_visited"
    );
    if (!hasSeenJourneyModal) {
      setShowJourneyModal(true);
    }
  }, []);

  const isEscalationContract =
    contract?.status === ECONTRACT_STATUS.DELIVERY_ESCALATION ||
    contract?.status === ECONTRACT_STATUS.INSPECTION_ESCALATION ||
    contract?.status === ECONTRACT_STATUS.RESOLVED_REFUND_CUSTOMER;

  useEffect(() => {
    if (isEscalationContract) setSelectedIndex(1);
  }, [isEscalationContract]);

  const handleCloseJourneyModal = useCallback(() => {
    setShowJourneyModal(false);
    // Save to localStorage so the modal doesn't show again
    localStorage.setItem("settlement_journey_visited", "true");
  }, []);

  // Create initial form data with CUSTOMER, PROVIDER, and BROKER arrays
  // Each array has the same length as the corresponding array in contract.partiesProfiles
  // Each entry is an object with value, fee, and userId properties
  const initialFormData: {
    CUSTOMER: Array<{
      value: number | undefined;
      userId: number;
    }>;
    PROVIDER: Array<{
      value: number | undefined;
      userId: number;
    }>;
    BROKER: Array<{
      value: number | undefined;
      userId: number;
    }>;
  } = {
    CUSTOMER: contract?.partiesProfiles?.CUSTOMER
      ? contract.partiesProfiles.CUSTOMER.map((profile) => {
          const party = contract.parties?.find(
            (p) => p.userId === String(profile.userIdentification)
          );

          return {
            value: isEscalationContract
              ? party?.amount || undefined
              : undefined,
            userId: profile.id || 0,
          };
        })
      : [],

    PROVIDER: contract?.partiesProfiles?.PROVIDER
      ? contract.partiesProfiles.PROVIDER.map((profile) => {
          const party = contract.parties?.find(
            (p) => p.userId === String(profile.userIdentification)
          );

          return {
            value: isEscalationContract
              ? undefined
              : party?.amount || undefined,
            userId: profile.id || 0,
          };
        })
      : [],

    BROKER: contract?.partiesProfiles?.BROKER
      ? contract.partiesProfiles.BROKER.map((profile) => {
          const party = contract.parties?.find(
            (p) => p.userId === String(profile.userIdentification)
          );

          return {
            value: isEscalationContract
              ? undefined
              : party?.amount || undefined,
            userId: profile.id || 0,
          };
        })
      : [],
  };

  const [formData, setFormData] = useState(initialFormData);

  const maxAmount = useMemo(() => {
    if (!contract) return 0;
    if (contract.type === EContractType.BROKERAGE_CONTRACT)
      return contract.itemPrice;
    let totalAmount = 0;
    const customers = contract.parties?.filter(
      (p) => p.role === ESenderRole.CUSTOMER
    );

    customers?.forEach((customer) => {
      let addOnFees = 0;
      const baseAmount = customer?.amount || 0;

      const currentCustomerAddOnFees = contract?.addonFeesList?.filter(
        (fee: AddOnFees) => fee?.payee === customer.phoneNumber
      );

      currentCustomerAddOnFees?.forEach((addOnFee) => {
        if (addOnFee.feeType === FeeCalculationType.FLAT) {
          addOnFees += addOnFee.amount;
        } else {
          addOnFees += (baseAmount * addOnFee.amount) / 100;
        }
      });

      totalAmount += baseAmount + addOnFees;
    });

    return totalAmount;
  }, [contract]);

  const currentTotalValues = useMemo(() => {
    let totalValues = 0;
    formData.BROKER.forEach((broker) => {
      totalValues += broker.value || 0;
    });
    formData.CUSTOMER.forEach((customer) => {
      totalValues += customer.value || 0;
    });
    formData.PROVIDER.forEach((provider) => {
      totalValues += provider.value || 0;
    });
    return totalValues;
  }, [formData]);

  const list = useMemo(() => {
    const receiversCount =
      (contract?.partiesProfiles?.BROKER?.length || 0) +
      (contract?.partiesProfiles?.PROVIDER?.length || 0);
    const payeesCount = contract?.partiesProfiles?.CUSTOMER?.length || 0;

    return [
      receiversCount > 0 && {
        id: 0,
        title: `${t("lbl_receivers")} (${receiversCount})`,
      },
      payeesCount > 0 && {
        id: 1,
        title: `${t("lbl_payees")} (${payeesCount})`,
      },
    ].filter(Boolean) as { id: number; title: string }[];
  }, [
    contract?.partiesProfiles?.BROKER?.length,
    contract?.partiesProfiles?.CUSTOMER?.length,
    contract?.partiesProfiles?.PROVIDER?.length,
    t,
  ]);

  const effectiveSelectedIndex = list.some((item) => item.id === selectedIndex)
    ? selectedIndex
    : list[0]?.id ?? 0;

  const partiesToRender = useMemo(() => {
    if (effectiveSelectedIndex === 0)
      return [...(formData?.BROKER || []), ...(formData?.PROVIDER || [])];
    return formData?.CUSTOMER;
  }, [formData, effectiveSelectedIndex]);

  const handleFormDataChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>, rowId: string, id: string) => {
      const newFormData = { ...formData };
      const currentParty = contract?.parties?.find((p) => p.userId === rowId);
      const currentPartyRole = currentParty?.role;
      const currentPartyIndex = formData[
        currentPartyRole || "CUSTOMER"
      ].findIndex((curr) => String(curr.userId) === id);
      newFormData[currentPartyRole || "CUSTOMER"][currentPartyIndex].value =
        e.target.value === "" ? undefined : Number(e.target.value);
      setFormData(newFormData);
      setShowSettlementError(false);
    },
    [contract?.parties, formData]
  );

  const handlebuttonClick = () => {
    if (
      Number(maxAmount).toFixed(2) !== Number(currentTotalValues).toFixed(2)
    ) {
      setShowSettlementError(true);
      return;
    }
    setConfirmationModal(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmationModal(false);
  };

  const handleConfirmSettlement = useCallback(async () => {
    if (!contract) return;

    const cashOutAmountList: {
      id: string;
      feePercentage?: number;
      amountDue: number;
    }[] = [];

    formData.BROKER.forEach((broker) => {
      if (broker.value) {
        const userIdentification = contract.partiesProfiles?.BROKER?.find(
          (br) => br.id === broker.userId
        )?.userIdentification;
        cashOutAmountList.push({
          id: String(userIdentification),
          amountDue: broker.value,
        });
      }
    });
    formData.PROVIDER.forEach((provider) => {
      if (provider.value) {
        const userIdentification = contract.partiesProfiles?.PROVIDER?.find(
          (pr) => pr.id === provider.userId
        )?.userIdentification;
        cashOutAmountList.push({
          id: String(userIdentification),
          amountDue: provider.value,
        });
      }
    });
    formData.CUSTOMER.forEach((customer) => {
      if (customer.value) {
        const userIdentification = contract.partiesProfiles?.CUSTOMER?.find(
          (cu) => cu.id === customer.userId
        )?.userIdentification;
        cashOutAmountList.push({
          id: String(userIdentification),
          amountDue: customer.value,
        });
      }
    });

    try {
      const body = {
        contractId: contract?.id,
        userId: userIdentification,
        actorRole: ESenderRole.CLIENT_ADMIN,
        senderId: String(contract?.senderId),
        contractAction: EContractActions.SETTLE_CONTRACT,
        contractType: contract.type,
        cashOutAmountList: cashOutAmountList,
      };
      await externalDoAction(body);
      navigate(`${ROUTES.CONTRACT}${contract?.id}`);
    } catch (error) {
      console.log("error", error);
    }
  }, [
    contract,
    formData.BROKER,
    formData.CUSTOMER,
    formData.PROVIDER,
    navigate,
    userIdentification,
  ]);

  const handleInfoClick = () => {
    setShowJourneyModal(true);
  };

  return (
    <div className={styles.container}>
      <Header handleInfoClick={handleInfoClick} />
      <div className={styles.contentContainer}>
        {showSettlementError && (
          <div style={{ marginBottom: "24px" }}>
            <AlertWidget
              activeAlertWidget={{
                id: 0,
                cta: {
                  enabled: false,
                  url: "",
                  label: "",
                },
                severity: "CRITICAL",
                createdAt: "",
                updatedAt: "",
                messageAr: `${t(
                  "lbl_distributed_amount"
                )} (${currentTotalValues}${t("lbl_saudi_currency")}) ${t(
                  "lbl_cash_in_amount"
                )} (${maxAmount}${t("lbl_saudi_currency")})`,
                descriptionAr: t("lbl_adjust_settlement"),
                surfaces: [],
                status: "ACTIVE",
              }}
            />
          </div>
        )}
        <BarChart
          currentTotalValues={currentTotalValues}
          maxAmount={maxAmount}
          isError={showSettlementError}
        />
        <div className={styles.tabContainer}>
          {list?.map((listItem) => (
            <li
              onClick={() => setSelectedIndex(listItem?.id)}
              key={listItem?.title}
              className={classNames(
                styles.listItem,
                {
                  [styles.listItemSelected]:
                    listItem?.id === effectiveSelectedIndex,
                },
                { [styles.firstItem]: listItem?.id === 0 },
                {
                  [styles.lastItem]:
                    listItem?.id === list?.[list?.length - 1]?.id,
                }
              )}>
              {listItem?.title}
            </li>
          ))}
        </div>
        <PartyTable
          detailsPage
          rows={(partiesToRender || [])?.map((party) => {
            const currentPartyProfile =
              contract?.partiesProfiles?.CUSTOMER?.find(
                (p) => p.id === party.userId
              ) ||
              contract?.partiesProfiles?.PROVIDER?.find(
                (p) => p.id === party.userId
              ) ||
              contract?.partiesProfiles?.BROKER?.find(
                (p) => p.id === party.userId
              );
            const currentParty = contract?.parties?.find(
              (partyObject) =>
                partyObject?.phoneNumber === currentPartyProfile?.phoneNumber ||
                (!!partyObject?.userId &&
                  partyObject?.userId ===
                    String(currentPartyProfile?.userIdentification))
            );
            return {
              id: party?.userId?.toString() || "",
              rowId: currentPartyProfile?.userIdentification?.toString() || "",
              name: currentPartyProfile?.profile?.fullName || "",
              phoneNumber: currentPartyProfile?.phoneNumber || "",
              amount:
                currentParty?.role === ESenderRole.CUSTOMER
                  ? 0
                  : currentParty?.amount || 0,
              formAmount: party.value,
              status:
                contract?.cashOutAmountList?.find(
                  (item) => item?.userIdentification === currentParty?.userId
                )?.settlementAmountStatus === EPARTY_STATUS.AVAILABLE_BALANCE
                  ? EPARTY_STATUS.AVAILABLE_BALANCE
                  : currentParty?.status,
              type:
                currentParty?.role === ESenderRole.CUSTOMER
                  ? "customer"
                  : "provider",
              fee: 0,
              total: +(currentParty?.fee || 0) + +(currentParty?.amount || 0),
              image: currentPartyProfile?.profile?.photoUrl || "",
            };
          })}
          columns={{
            showActions: false,
            showStatus: false,
            showRole: false,
            showFee: false,
            showTotal: false,
            showInputField: true,
          }}
          getIndexByRow={(row) =>
            (contract?.parties || [])?.findIndex(
              (p) => p?.id?.toString() === row?.id
            )
          }
          inputFieldProps={{
            maxAmount: maxAmount,
            handleChange: handleFormDataChange,
          }}
        />

        <div className={styles.cardsContainer}>
          <AmountCard
            title={t("lbl_value_assigned_to_you")}
            amount={String(
              formData.BROKER.find((br) => br.userId === accountId)?.value ||
                "0"
            )}
            style={{ backgroundColor: "#F8F9FB" }}
            isSmall
          />
          <AmountCard
            title={t("lbl_waffy_fees_amount")}
            amount={String(contract?.fee || "0")}
            style={{ backgroundColor: "#F8F9FB" }}
            isSmall
          />
          <AmountCard
            title={t("lbl_your_profit")}
            amount={String(
              (formData.BROKER.find((br) => br.userId === accountId)?.value ||
                0) - (contract?.fee || 0)
            )}
            style={{
              backgroundColor:
                (formData.BROKER.find((br) => br.userId === accountId)?.value ||
                  0) -
                  (contract?.fee || 0) ===
                0
                  ? "#F8F9FB"
                  : undefined,
            }}
            specialStyle={
              (formData.BROKER.find((br) => br.userId === accountId)?.value ||
                0) -
                (contract?.fee || 0) >
              0
                ? "positive"
                : (formData.BROKER.find((br) => br.userId === accountId)
                    ?.value || 0) -
                    (contract?.fee || 0) <
                  0
                ? "negative"
                : undefined
            }
            isSmall
          />
        </div>
        <PrimaryButton
          disabled={
            !formData.BROKER.some((br) => br.value !== undefined) &&
            !formData.CUSTOMER.some((cr) => cr.value !== undefined) &&
            !formData.PROVIDER.some((pr) => pr.value !== undefined)
          }
          onClick={handlebuttonClick}
          style={{
            width: "370px",
            height: "44px",
            borderRadius: "8px",
            textAlign: "center",
            whiteSpace: "nowrap",
            fontSize: "14px",
            lineHeight: "100%",
            fontWeight: 500,
            alignSelf: "flex-end",
            marginTop: "62px",
            background:
              !formData.BROKER.some((br) => br.value !== undefined) &&
              !formData.CUSTOMER.some((cr) => cr.value !== undefined) &&
              !formData.PROVIDER.some((pr) => pr.value !== undefined)
                ? "#E2E5EE"
                : "#0D44AD",
          }}>
          {t("lbl_confirm")}
        </PrimaryButton>
      </div>
      {confirmationModal && (
        <SettlementConfirmationModal
          modalOpen={confirmationModal}
          handleClose={handleCloseConfirmationModal}
          currentTotalValues={currentTotalValues}
          arbitratorAmount={
            formData.BROKER.find((br) => br.userId === accountId)?.value || 0
          }
          waffyFees={contract?.fee || 0}
          handleConfirmSettlement={handleConfirmSettlement}
        />
      )}
      {showJourneyModal && (
        <SettlementJourneyModal
          modalOpen={showJourneyModal}
          handleClose={handleCloseJourneyModal}
        />
      )}
    </div>
  );
};

export default SettlementDetails;
